import {X} from "lucide-react"
import {Dialog as DialogPrimitive} from "radix-ui"
import type React from "react"

import {cn} from "@/lib/cn"

import {iconButtonClassName} from "./icon-button"

/**
 * DIZED modal on Radix Dialog: surface gradient, lit edge, r16, centered.
 * Replaces the react-bootstrap Modal.
 */
export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close
export const DialogPortal = DialogPrimitive.Portal

export const DialogOverlay = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className,
    )}
    {...props}
  />
)

export const DialogContent = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      className={cn(
        "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2",
        // cap to the viewport and scroll internally so tall modals (long
        // descriptions, forms) aren't cut off the top/bottom on short screens
        "max-h-[calc(100dvh-2rem)] overflow-y-auto",
        "rounded-list card-elev text-foreground shadow-card",
        "p-5 outline-none",
        "duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        // The beta2 round control, so it stays legible over cover art.
        className={cn("absolute right-3 top-3 z-10", iconButtonClassName)}
        aria-label="Close"
      >
        <X className="size-4" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
)

export const DialogHeader = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mb-3 flex flex-col gap-1 pr-8", className)} {...props} />
)

export const DialogTitle = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title className={cn("font-display text-lg font-bold", className)} {...props} />
)

export const DialogDescription = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    className={cn("font-sans text-sm text-muted-foreground", className)}
    {...props}
  />
)

export const DialogFooter = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-4 flex flex-wrap items-center justify-end gap-2", className)} {...props} />
)
